import {
  BrowserCacheLocation,
  EventType,
  PublicClientApplication,
  type Configuration,
  type RedirectRequest,
} from '@azure/msal-browser'

let tokenExpirationTimer: any
let msalConfig: Configuration
let msalInstance: PublicClientApplication
let initialized = false
export const useMSAuth = () => {
  const api = useApi()

  async function initialize() {
    let scopes, config
    
    const response = await api.public.get();
    const publicConfigEntra = response.auth.msEntra
    if(!publicConfigEntra) {
      return
    }
    scopes = publicConfigEntra.scopes
    config = { ...publicConfigEntra, scopes: undefined }

    msalConfig = {
      auth: {
        ...config,
        navigateToLoginRequestUrl: true,
        OIDCOptions: {
          defaultScopes: scopes?.split(' ') ?? [],
        },
      },
      cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage,
        storeAuthStateInCookie: true,
      },
      system: {
        tokenRenewalOffsetSeconds: 300,
      },
    }

    msalInstance = new PublicClientApplication(msalConfig)
    window.msal = msalInstance

    await msalInstance.initialize()

    // Handle redirect promise after login or redirect
    await msalInstance
      .handleRedirectPromise() // Handles the redirect promise and obtains the response
      .then(handleResponse)
      .catch((err) => {
        throw new Error(err)
      })

    // Add event callback for login success
    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        setupTokenExpirationTimer()
      }
    })

    initialized = true
  }

  // Handle the response after login or redirect
  function handleResponse(resp: any) {
    if (resp?.account) {
      setupTokenExpirationTimer()
    }
  }

  // Set up timer for refreshing access token upon expiration
  function setupTokenExpirationTimer() {
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length > 0) {
      const account = accounts[0]
      if (account.idTokenClaims && account.idTokenClaims.exp) {
        const tokenExpirationTime = account.idTokenClaims.exp * 1000
        const currentTime = Date.now()
        const timeUntilExpiration = tokenExpirationTime - currentTime

        clearTimeout(tokenExpirationTimer)

        tokenExpirationTimer = setTimeout(() => {
          refreshAccessToken(account)
        }, timeUntilExpiration)
      }
    }
  }

  // Refresh access token
  async function refreshAccessToken(account: any) {
    try {
      const response = await msalInstance.acquireTokenSilent({
        account,
        scopes: msalConfig.auth.OIDCOptions.defaultScopes,
      })
      setupTokenExpirationTimer()
    } catch (err) {
      console.error('Token refresh error:', err)
      //signOut(account.homeAccountId);
    }
  }

  // Sign in with redirect
  async function signIn(params: Partial<RedirectRequest> = {}) {
    console.log(msalConfig)
    try {
      await msalInstance.loginRedirect({
        scopes: msalConfig.auth.OIDCOptions.defaultScopes,
        ...params,
      })
    } catch (err) {
      console.log('Login error:', err)
    }
  }

  // Acquire access token silently
  async function acquireTokenSilent() {
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length > 0) {
      const account = accounts[0]
      msalInstance.setActiveAccount(account)
      try {
        const response = await msalInstance.acquireTokenSilent({
          account,
          scopes: msalConfig.auth.OIDCOptions.defaultScopes,
        })
        return response.accessToken
      } catch (err) {
        return null
      }
    } else {
      throw new Error('No accounts found')
    }
  }

  // Get all MSAL accounts
  function getAccounts() {
    return msalInstance.getAllAccounts()
  }

  function getActiveAccount() {
    return msalInstance.getActiveAccount()
  }

  // Check if user is authenticated
  function isAuthenticated() {
    return !!getActiveAccount()
  }

  // Sign out user
  async function signOut() {
    return await msalInstance.logoutRedirect({
      account: msalInstance.getActiveAccount(),
      onRedirectNavigate: () => false,
    })
  }

  return {
    initialized,
    initialize,
    msalInstance,
    signIn,
    getAccounts,
    acquireTokenSilent,
    isAuthenticated,
    signOut,
  }
}
